'use client';

import classNames from 'classnames';
import { ChangeEvent, FC, FocusEventHandler, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PropsWithClassname } from '@/app/types';

import Select from '@/app/components/Select/Select';
import { isNumber } from '@/app/utils/numbers';

const getYearOptions = (startYear: number = 1900, endYear: number = 2020) => {
  return new Array(Math.abs(endYear - startYear) + 1)
    .fill(startYear)
    .map((year, index) => ({ value: year + index, label: year + index }));
};

const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export interface DatePickerProps extends PropsWithClassname {
  name: string;
  value?: Date;
  onChange: (nextDate: Date | undefined) => void;
  onBlur?: FocusEventHandler<HTMLSelectElement>;
  label?: string;
  disabled?: boolean;
  isErrorVisible?: boolean;
  errorMessage?: ReactNode;
  note?: string;
  startYear?: number;
  endYear?: number;
}

const parseValue = (value: string) => {
  const intValue = parseInt(value);

  return isNaN(intValue) ? undefined : intValue;
};

const DatePicker: FC<DatePickerProps> = ({
  name,
  onChange,
  onBlur,
  label,
  disabled,
  isErrorVisible,
  errorMessage,
  note,
  startYear,
  endYear,
  value,
  className,
}) => {
  const { t } = useTranslation();

  const [year, setYear] = useState<number | undefined>(value?.getFullYear?.() ?? undefined);
  const [month, setMonth] = useState<number | undefined>(value?.getMonth?.() ?? undefined);
  const [day, setDay] = useState<number | undefined>(value?.getDate?.() ?? undefined);

  useEffect(() => {
    if (!isNumber(year) || !isNumber(month) || !isNumber(day)) {
      return onChange(undefined);
    }

    const nextDate = new Date(year, month, day, 12);
    onChange(nextDate);
  }, [year, month, day]);

  const yearOptions = useMemo(
    () => [{ value: undefined, label: t('components.DatePicker.year') }, ...getYearOptions(startYear, endYear)],
    [startYear, endYear, t],
  );

  const monthOptions = useMemo(() => {
    return [
      { value: undefined, label: t('components.DatePicker.month') },
      { value: 0, label: t('enums.months.january') },
      { value: 1, label: t('enums.months.february') },
      { value: 2, label: t('enums.months.march') },
      { value: 3, label: t('enums.months.april') },
      { value: 4, label: t('enums.months.may') },
      { value: 5, label: t('enums.months.june') },
      { value: 6, label: t('enums.months.july') },
      { value: 7, label: t('enums.months.august') },
      { value: 8, label: t('enums.months.september') },
      { value: 9, label: t('enums.months.october') },
      { value: 10, label: t('enums.months.november') },
      { value: 11, label: t('enums.months.december') },
    ];
  }, [t]);

  const handleYearChange = useCallback(
    (ev: ChangeEvent<HTMLSelectElement>) => setYear(parseValue(ev.target.value)),
    [],
  );

  const handleMonthChange = useCallback(
    (ev: ChangeEvent<HTMLSelectElement>) => setMonth(parseValue(ev.target.value)),
    [],
  );

  const handleDayChange = useCallback((ev: ChangeEvent<HTMLSelectElement>) => setDay(parseValue(ev.target.value)), []);

  const dayOptions = useMemo(() => {
    const days = Array(month ? daysInMonth[month] : 31)
      .fill(1)
      .map((item, index) => ({ value: item + index, label: item + index }));

    return [{ value: undefined, label: t('components.DatePicker.day') }, ...days];
  }, [month]);

  return (
    <div className={classNames(className, { 'has-error': isErrorVisible })} data-testid={`form-field-${name}`}>
      {!!label && <label className="block mb-[8px] font-medium text-[16px] leading-[24px]">{label}</label>}
      {!!errorMessage && isErrorVisible && <div className="text-warning font-medium mb-[8px]">{errorMessage}</div>}
      <div className="flex">
        <Select<number>
          name={`${name}-day`}
          options={dayOptions}
          disabled={disabled}
          onChange={handleDayChange}
          onBlur={onBlur}
          value={day}
          isErrorVisible={isErrorVisible}
        />
        <div className="px-[8px] pt-[12px]">-</div>
        <Select<number>
          name={`${name}-month`}
          options={monthOptions}
          disabled={disabled}
          onChange={handleMonthChange}
          onBlur={onBlur}
          value={month}
          isErrorVisible={isErrorVisible}
        />
        <div className="px-[8px] pt-[12px]">-</div>
        <Select<number>
          name={`${name}-year`}
          options={yearOptions}
          disabled={disabled}
          onChange={handleYearChange}
          onBlur={onBlur}
          value={year}
          // TODO
          isErrorVisible={isErrorVisible}
        />
      </div>
      {!!note && <small className="block text-[12px] leading-[1.4] text-neutral-mid mb-[32px]">{note}</small>}
    </div>
  );
};

export default DatePicker;
